import { G1, G7 } from './drag-curves.ts';

export interface TrajectoryOptions {
  zeroRange?: number;
  latitude?: number;
  longitude?: number;
  azimuth?: number;
  bc: number;
  bulletDiameter?: number;
  bulletLength?: number;
  bulletWeight?: number;
  dragCurve: 'G1' | 'G7';
  gunElevationAngleMinutes?: number;
  humidity: number;
  includeRanges?: number[];
  lookAngle: number;
  cant?: number;
  pressure: number;
  range: number;
  rangeStep: number;
  remoteWind?: number;
  remoteWindDir?: number;
  scopeHeight: number;
  temperature: number;
  twistDirection: 1 | -1;
  twistRate: number;
  useAerodynamicJump: boolean;
  useSpinDrift: boolean;
  useOldSpinDrift: boolean;
  v0: number;
  wind: number | { speed: number; direction: number; range: number }[] | ((v: number) => [number, number]);
  windDir: number;
}

let M_G7 = G7.map((v) => v[0]);
let D_G7 = G7.map((v) => v[1]);
let M_G1 = G1.map((v) => v[0]);
let D_G1 = G1.map((v) => v[1]);

const metersToFeet = (meters) => meters * 3.28084;

let GRAVITY = 32.174;
//const GRAVITY = metersToFeet(9.832) // north pole
//const GRAVITY = metersToFeet(9.7639) // Mount Nevado Huascarán in Peru
const EARTH_ROTATION_RATE = 0.00007292;
const degToRad = (deg) => (deg * Math.PI) / 180;
const inchToMoa = (inches, yards) => inches / (yards / 100) / 1.0472;
const inchToMils = (inches, yards) => inchToMoa(inches, yards) / 3.43774677;
const moaToInch = (moa, yards) => (yards / 100) * moa * 1.0472;
const mradToRad = (mrad) => mrad / 1000;
const inchToYards = (inches) => inches / 36;
const yardsToInch = (yards) => yards * 36;
const yardsToFeet = (yards) => yards * 3;

export default function trajectory(options_: Partial<TrajectoryOptions>) {
  const options = sanitizeOptions(options_);
  const { zeroRange } = options;
  let result;
  if (zeroRange) {
    const zeroTrajectory = trajectoryCalculate({
      ...options,
      rangeStep: zeroRange,
      range: zeroRange,
      latitude: NaN,
      longitude: NaN,
      azimuth: NaN,
      useSpinDrift: false,
      useAerodynamicJump: false,
    });
    const gunElevationAngleMinutes = inchToMoa(-zeroTrajectory[zeroRange].drop, zeroRange);
    result = trajectoryCalculate({
      ...options,
      gunElevationAngleMinutes,
    });
    if (result[zeroRange] && Math.abs(result[zeroRange].drop) <= 0.1) {
      result[zeroRange].drop = 0;
      result[zeroRange].dropMOA = 0;
    }
  } else {
    result = trajectoryCalculate(options);
  }
  if (result[0]) {
    result[0].dropMOA = 0;
    result[0].windMOA = 0;
  }
  return result;
}

type Trajectory = {
  range: number;
  energy: number;
  drop: number;
  dropMOA: number;
  wind: number;
  windMOA: number;
  velocity: number;
  windvelocity: number;
  time: number;
  sg?: number;
};

export function trajectoryCalculate(options: TrajectoryOptions): {
  [range: string]: Trajectory;
} {
  const {
    azimuth,
    bc,
    bulletDiameter,
    bulletLength,
    bulletWeight,
    dragCurve,
    gunElevationAngleMinutes,
    humidity,
    includeRanges,
    latitude,
    longitude,
    lookAngle,
    cant,
    pressure,
    range,
    rangeStep,
    remoteWind,
    remoteWindDir,
    scopeHeight,
    temperature,
    twistDirection,
    twistRate,
    useAerodynamicJump,
    useSpinDrift,
    v0,
    wind,
    windDir,
    zeroRange,
  } = options;
  let gravity = GRAVITY;
  if (latitude && longitude) {
    //gravity = lookupGravity(latitude, longitude)
  }
  const stdDensity = airDensity(29.92, 59, 0);
  const trajDensity = airDensity(pressure, temperature, humidity);
  const altitude = altFromPressureAndTemperature(pressure, temperature);
  const M = dragCurve === 'G1' ? M_G1 : M_G7;
  const D = dragCurve === 'G1' ? D_G1 : D_G7;
  let R0 = trajDensity / stdDensity;
  let W1;
  let W3;
  let W1mph;
  let W3mph;
  let muzzleW3mph;
  let getWind;
  if (typeof wind === 'function') {
    getWind = wind;
  } else if (Array.isArray(wind) && wind.length) {
    const windCopy = [...wind];
    let currentWind = windCopy.shift();
    getWind = (range) => {
      if (windCopy.length && range >= windCopy[0].range) {
        currentWind = windCopy.shift();
      }
      return [currentWind.speed, currentWind.direction];
    };
  } else if (remoteWind) {
    getWind = (r) => {
      if (r < range / 2) {
        return [wind, windDir];
      } else {
        return [remoteWind, remoteWindDir];
      }
    };
  } else {
    getWind = (range) => {
      return [wind, windDir];
    };
  }
  const windFn = (range) => {
    const [wind, windDir] = getWind(range);
    W1mph = wind * Math.cos(degToRad(windDir) + Math.PI); // positive = gun to target
    W3mph = wind * Math.sin(degToRad(windDir) + Math.PI); // positive = left to right
    if (muzzleW3mph === undefined) {
      muzzleW3mph = W3mph;
    }
    W1 = (22 * W1mph) / 15;
    W3 = (22 * W3mph) / 15;
    return [W1, W3];
  };
  [W1, W3] = windFn(0);
  let launchSg;
  if (bulletDiameter && bulletLength && bulletWeight && twistRate) {
    launchSg = sg(bulletDiameter, bulletLength, bulletWeight, v0, altitude, twistRate);
  } else {
    launchSg = 1.5;
  }

  let coriolisGravityCorrection = 1;
  if (!isNaN(latitude) && !isNaN(azimuth)) {
    coriolisGravityCorrection =
      1 - ((2 * EARTH_ROTATION_RATE * v0) / GRAVITY) * Math.cos(degToRad(latitude)) * Math.sin(degToRad(azimuth));
  }
  gravity = gravity * coriolisGravityCorrection;
  let forwardGravity = 0;
  let windGravity = 0;
  if (lookAngle) {
    const gravityCorrection = Math.cos(degToRad(lookAngle));
    gravity = GRAVITY * gravityCorrection * coriolisGravityCorrection;
    const forwardGravityCorrection = Math.sin(degToRad(lookAngle));
    forwardGravity = GRAVITY * forwardGravityCorrection;
  }
  if (cant) {
    windGravity = Math.sin(degToRad(cant)) * GRAVITY;
  }
  let E1 = 0.00001;
  let P = {};
  let Q = {};
  let R = {};
  let T = {};
  let V = {};
  let W = {};
  let X = {};
  let Y = {};
  let Z = {};
  let UC = 3;
  let DINT = 1; // distance integration = 1 yard
  let D3 = DINT * UC;
  let RH1 = -0.00003158;
  let RH2 = 0;
  let TK1 = -0.000006015;
  let TK2 = 0;
  let PIR = -0.000208551;
  let VV1 = 49.19;
  let N = 2;
  let PR2 = 0;
  let V3 = v0 * Math.cos(gunElevationAngleMinutes / 3437.74677);
  let V4 = v0 * Math.sin(gunElevationAngleMinutes / 3437.74677);
  let V5 = 0;
  let R1 = 0;
  let H1 = -scopeHeight / 12;
  let D1 = 0;
  let T1 = 0;
  let spinDriftVelocity = 0;
  if (useSpinDrift) {
    spinDriftVelocity = -twistDirection * (0.5349 * launchSg + 0.642) * DINT;
  }
  Q[0] = R1;
  R[0] = -scopeHeight;
  T[0] = D1;
  V[0] = v0;
  W[0] = T1;
  X[0] = V3;
  Y[0] = V4;
  Z[0] = V5;
  let rpm = (720 * v0) / parseFloat(twistRate)
  let C3 = (PIR * R0) / bc;
  let B1 = Math.sqrt(Math.pow(V3 - W1, 2) + Math.pow(V4, 2) + Math.pow(V5 - W3, 2));
  let T0 = (temperature + 459.67) * Math.exp((TK1 + TK2 * H1) * H1) - 459.67;
  let V1 = VV1 * Math.sqrt(T0 + 459.67);
  let X1 = B1 / V1;
  let C1 = interpolate(X1, M, D);
  let C4 = (C3 * C1 * B1 * Math.exp((RH1 + RH2 * H1) * H1)) / V3;
  let A1 = C4 * (V3 - W1);
  let A2 = C4 * V4 - gravity / V3;
  let A3 = C4 * (V5 - W3);
  while (PR2 < range) {
    if (lookAngle) {
      // NOTE this is valid if lookAngle is 0, and should probably be enabled for that at some point
      const relativeAltitude = PR2 * Math.sin(degToRad(lookAngle)) * D3 + H1;
      const altitudeDensity = pressureAtAlt(relativeAltitude, trajDensity, temperature);
      R0 = altitudeDensity / stdDensity;
      C3 = (PIR * R0) / bc;
    }
    let R2 = R1 + D3;
    //console.log({ R2, PR2 })
    [W1, W3] = windFn(PR2);
    PR2 += DINT;
    let V6 = V3 + A1 * D3;
    let V7 = V4 + A2 * D3;
    let V8 = V5 + A3 * D3 + spinDriftVelocity;
    let B2 = Math.sqrt(Math.pow(V6 - W1, 2) + Math.pow(V7, 2) + Math.pow(V8 - W3, 2));
    let A4, A5, A6, E2;
    let doCount = 0;
    do {
      ++doCount;
      var U = B2;
      T0 = (temperature + 459.67) * Math.exp((TK1 + TK2 * H1) * H1) - 459.67;
      V1 = VV1 * Math.sqrt(T0 + 459.67);
      X1 = B2 / V1;
      let C2 = interpolate(X1, M, D);
      let C5 = (C3 * C2 * B2 * Math.exp((RH1 + RH2 * H1) * H1)) / V6;
      A4 = C5 * (V6 - W1) - forwardGravity / V6; // forward
      A5 = C5 * V7 - gravity / V6; // wind
      A6 = C5 * (V8 - W3) + windGravity / V6; // wind
      V6 = V3 + 0.5 * (A1 + A4) * D3; // forward
      V7 = V4 + 0.5 * (A2 + A5) * D3; // vertical
      V8 = V5 + 0.5 * (A3 + A6) * D3; // wind
      B2 = Math.sqrt(Math.pow(V6 - W1, 2) + Math.pow(V7, 2) + Math.pow(V8 - W3, 2));
      E2 = Math.abs((B2 - U) / B2);
    } while (doCount < 10 && E2 > E1);
    let H2 = H1 + ((V4 + V7) / (V3 + V6)) * D3;
    const dropAngle = Math.tan((12 * (H1 - H2)) / (D3 * 12));
    if (Math.abs(lookAngle) > 0) {
      gravity = GRAVITY * Math.cos(degToRad(lookAngle) - dropAngle) * coriolisGravityCorrection;
      forwardGravity = GRAVITY * Math.sin(degToRad(lookAngle) - dropAngle);
      // this accounts for the angle of flight of the bullet,
      // but it's not a standardized calc, so leaving off for now for trajectories without look angle
      //} else {
      //gravity = GRAVITY * Math.cos(dropAngle) * coriolisGravityCorrection
      //forwardGravity = GRAVITY * Math.sin(dropAngle)
    }
    let D2 = D1 + ((V5 + V8) / (V3 + V6)) * D3;
    let dt = (2 * D3) / (V3 + V6);
    let T2 = T1 + dt
    let V2 = Math.sqrt(Math.pow(V6, 2) + Math.pow(V7, 2) + Math.pow(V8, 2));

    /* Use this to calculate roll decay
    const Ix = 0.673
    const Iy = 5.351
    const clp_a = (M) => -0.0013041 * M ** 2 + 0.0131526 * M - 0.0505272
    const clp_b = (M) =>  0.0037958 * M ** 2 - 0.0387777 * M + 0.1521346
    const clp_c = (M) => -0.0003363 * M ** 2 + 0.0035027 * M - 0.0141904
    const clp_d = (M) => -0.0001407 * M ** 2 + 0.0015084 * M - 0.0064200
    const est_Clp = (M, Ix, diam, l) => clp_a(M) + clp_b(M) * diam + clp_c(M) * Ix + clp_d(M) * (l / diam)

    const Clp = est_Clp(X1, Ix, bulletDiameter, bulletLength)
    //const spinMoment = .5 * 
    const mps = V2 * .3048
    let rads = rpm * .10472
    const rho = trajDensity
    const diameterMeters = bulletDiameter * .0254
    const area = Math.PI * Math.pow(diameterMeters / 2, 2)
    const Iy_si = Iy / 100000
    const Clp_hat = (rho * mps * area * diameterMeters ** 2 * Clp * rads) / (2 * Iy_si)
    const dodt = Clp_hat * mps
    const dodt2 = dodt * dt
    const newRads = rads + dodt2
    const newRpm = newRads / .10472
    rpm = newRpm
    //console.log({ trajDensity, mps, rpm, rads, dt, T2, newRads, newRpm, diameterMeters, area, Clp_hat, dodt, dodt2 })
    if (N % 100 === 0) {
      const effectiveTwistRate = (720 * V2) / rpm
      const newSg = sg(bulletDiameter, bulletLength, bulletWeight, V2, altitude, effectiveTwistRate);
      console.log(T2, N, rads, rpm, Clp, effectiveTwistRate, newSg)
    }
    */


    //  RESET CONDITIONS AT R1 TO NEW CONDITIONS AT R2.
    R1 = R2;
    H1 = H2;
    D1 = D2;
    T1 = T2;
    V3 = V6;
    V4 = V7;
    V5 = V8;
    A1 = A4;
    A2 = A5;
    A3 = A6;
    let P5 = PR2;
    let P6 = 12 * H2;
    let P7 = 12 * D2;
    if (N % rangeStep === 0 || N === range || (includeRanges && includeRanges.includes(N))) {
      P[N] = X1;
      Q[N] = P5;
      R[N] = P6;
      T[N] = P7;
      V[N] = V2;
      W[N] = T2;
      X[N] = V6;
      Y[N] = V7;
      Z[N] = V8;
    }
    N += DINT;
  }
  const result = {};
  for (const range in Q) {
    let energy;
    if (bulletWeight) {
      energy = (V[range] * V[range] * bulletWeight) / 450240;
    }
    result[range] = {
      range,
      mach: P[range],
      energy,
      drop: R[range],
      wind: T[range],
      velocity: V[range],
      windvelocity: Z[range],
      dropVelocity: Y[range],
      time: W[range],
    };
    if (range === '0') {
      result[range].sg = launchSg;
    }
  }
  let maxDropMils = 0
  for (const range in result) {
    if (options.useOldSpinDrift) {
      const drift = spinDrift(launchSg, result[range].time) * twistDirection;
      result[range].wind += drift;
    }
    if (bulletLength && useAerodynamicJump) {
      const jump = aerodynamicJump(launchSg, bulletLength, muzzleW3mph) * twistDirection;
      result[range].drop += jump * moaToInch(1, +range);
    }
    if (!isNaN(latitude)) {
      const horizCoriolis = EARTH_ROTATION_RATE * (+range * 3) * Math.sin(degToRad(latitude)) * result[range].time * 12;

      if (isFinite(horizCoriolis)) {
        result[range].wind += horizCoriolis;
      }
    }
    result[range].windMOA = inchToMoa(result[range].wind, range);
    result[range].dropMOA = inchToMoa(result[range].drop, range);
    result[range].windMils = inchToMils(result[range].wind, range);
    result[range].dropMils = inchToMils(result[range].drop, range);
    if (isFinite(result[range].dropMils) && range > zeroRange) {
      maxDropMils = Math.min(maxDropMils, result[range].dropMils)
    }
  }
  // find max ordinate from result
  let maxOrdinate = 0;
  let maxOrdinateRange = 0;
  let dropAngle = mradToRad(-maxDropMils)
  for (const range in result) {
    // rotate drop by dropAngle
    const r = +range;
    const originalDrop = inchToYards(result[range].drop);
    const adjustedDrop = originalDrop * Math.cos(dropAngle) + r * Math.sin(dropAngle);
    if (adjustedDrop > maxOrdinate) {
      maxOrdinate = adjustedDrop;
      maxOrdinateRange = r;
    }
    result[range].aboveLos = yardsToInch(adjustedDrop)
  }
  result[maxOrdinateRange].maxOrdinate = true
  return result;
}

export function interpolate(X1: number, M: number[], D: number[]): number {
  let I = 1;
  while (M[I + 1]) {
    if (X1 < M[I + 1]) {
      break;
    }
    I += 1;
  }
  if (I === M.length - 1) {
    return D[I];
  }
  let S = (D[I + 1] - D[I]) / (M[I + 1] - M[I]);
  let X2 = D[I] + S * (X1 - M[I]);
  return X2;
}

// https://www.brisbanehotairballooning.com.au/calculate-air-density/
// this is superceded by the new formula that accounts for humidity, but keeping it for reference
// function airDensity(pressure, temperature) {
//   const kelvin = ((temperature - 32) * 5) / 9 + 273.15
//   const pa = pressure * 3386
//   const gasConstant = 287.05
//   return pa / (gasConstant * kelvin)
// }

// https://collegedunia.com/exams/density-of-air-units-formulas-calculations-and-factors-mathematics-articleid-1794
// verified with https://www.omnicalculator.com/physics/air-density
function airDensity(pressure: number, temperature: number, relHumidity: number): number {
  if (relHumidity < 0) {
    relHumidity = 0;
  }
  if (relHumidity > 100) {
    relHumidity = 100;
  }
  const celcius = ((temperature - 32) * 5) / 9;
  const kelvin = celcius + 273.15;
  const p1 = 6.1078 * Math.pow(10, (7.5 * celcius) / (celcius + 237.3));
  const pv = p1 * relHumidity;
  const pd = pressure * 3386 - pv;
  const rho = pd / (287.058 * kelvin) + pv / (461.495 * kelvin);
  return rho;
}

// AB appendix
function spinDrift(sg, tof) {
  return 1.25 * (sg + 1.2) * Math.pow(tof, 1.83);
}

function sg(
  bulletDiameter: number,
  length: number,
  grains: number,
  velocity: number,
  altitude: number,
  twistRate: number
): number {
  const calibersLength = length / bulletDiameter;
  const twistPerCalibers = twistRate / bulletDiameter;
  let correction = 1;
  if (velocity) {
    correction /= Math.pow(velocity / 2800, 0.3333);
  }
  if (altitude) {
    correction /= Math.pow(Math.E, 3.158e-5 * altitude);
  }
  let sg =
    (30 * grains) /
    (Math.pow(twistPerCalibers, 2) * Math.pow(bulletDiameter, 3) * calibersLength * (1 + Math.pow(calibersLength, 2)));
  sg /= correction;
  return sg;
}

function altFromPressureAndTemperature(pressure: number, temperature: number): number {
  return (3.28 * (Math.pow(29.92 / pressure, 1 / 5.257) - 1) * (temperature + 273.15)) / 0.0065;
}

// https://www.omnicalculator.com/physics/air-pressure-at-altitude#how-to-calculate-air-pressure-at-altitude
function pressureAtAlt(altitude, referencePressure, temperature) {
  const celcius = ((temperature - 32) * 5) / 9;
  const kelvin = celcius + 273.15;
  const m = 0.0289644;
  const g = 9.80665;
  const r = 8.3144598;
  const h = altitude / 3.2808;
  const p0 = referencePressure;
  const t = kelvin;
  const pressure = p0 * Math.exp(-(m * g * h) / (r * t));
  return pressure;
}

// AB appendix
function aerodynamicJump(sg: number, length: number, wind: number): number {
  return (0.01 * sg - 0.0024 * length + 0.032) * -wind;
}

function sanitizeOptions(options_: Partial<TrajectoryOptions>): TrajectoryOptions {
  const options = { ...options_ };
  const floatDefaults = {
    pressure: 29.92,
    temperature: 59,
    humidity: 0,
    bc: 0.3,
    scopeHeight: 1.5,
    gunElevationAngleMinutes: 0,
    lookAngle: 0,
  };
  const intDefaults = {
    v0: 2700,
    rangeStep: 100,
    range: 1000,
  };
  for (const key in floatDefaults) {
    if (options.hasOwnProperty(key)) {
      options[key] = parseFloat(options[key]);
    }
    if (isNaN(options[key])) {
      options[key] = floatDefaults[key];
    }
  }
  for (const key in intDefaults) {
    if (options.hasOwnProperty(key)) {
      options[key] = parseInt(options[key]);
    }
    if (isNaN(options[key])) {
      options[key] = intDefaults[key];
    }
  }
  if (options.hasOwnProperty('latitude')) {
    if (options.latitude > 90) {
      options.latitude = 90;
    }
    if (options.latitude < -90) {
      options.latitude = -90;
    }
  }
  if (options.hasOwnProperty('longitude')) {
    if (options.longitude > 180) {
      options.longitude = 180;
    }
    if (options.longitude < -180) {
      options.longitude = -180;
    }
  }
  if (options.hasOwnProperty('azimuth')) {
    if (options.azimuth > 360) {
      options.azimuth = 360;
    }
    if (options.azimuth < 0) {
      options.azimuth = 0;
    }
  }
  //if (options.twistDirection !== -1 && options.twistDirection !== '-1') {
  if (options.twistDirection !== -1) {
    options.twistDirection = 1;
  } else {
    options.twistDirection = -1;
  }
  if (!Array.isArray(options.includeRanges)) {
    options.includeRanges = [];
  }
  if (options.useAerodynamicJump !== false) {
    options.useAerodynamicJump = true;
  }
  if (options.useSpinDrift !== false) {
    options.useSpinDrift = true;
  }
  if (options.dragCurve !== 'G1') {
    options.dragCurve = 'G7';
  }
  if (!options.hasOwnProperty('wind')) {
    options.wind = 0;
    options.windDir = 0;
  }
  if (Array.isArray(options.wind) && !options.wind.length) {
    options.wind = 0;
    options.windDir = 0;
  }
  if (Array.isArray(options.wind) && options.wind.length) {
    options.wind = options.wind.map(wind => {
      const parsed = {}
      if (wind.speed) parsed.speed = parseFloat(wind.speed)
      if (isNaN(parsed.speed)) parsed.speed = 0
      if (wind.direction) parsed.direction = parseFloat(wind.direction)
      if (isNaN(parsed.direction)) parsed.direction = 0
      if (wind.range) parsed.range = parseFloat(wind.range)
      if (isNaN(parsed.range)) parsed.range = 0
      return parsed
    })
  }
  return options as TrajectoryOptions;
}

/*
const gs = {
  actual: 32.174,
  'nepal actual': 31.50732454500114, // nepal actual
  //'nepal estimate': 32.00276278191815, // nepal estimate
  'utah actual': 32.130778699679674,
  //'utah estimate': 31.909207142068986,
  'cali actual': 32.160713782282194,
  //'cali estimate': 32.091950737593834,
  whittington: 32.12941720738424,
}
let options = {} as TrajectoryOptions
//options.zeroRange = 100
options.latitude = 0
options.longitude = 0
options.azimuth = 0
options.bc = 0.475
options.bulletDiameter = 0.416
options.bulletLength = 2.385
options.bulletWeight = 230
options.dragCurve = 'G7'
options.humidity = 28
options.lookAngle = 0
options.pressure = 25
options.range = 1000
options.scopeHeight = 2
options.temperature = 56
options.twistDirection = 1
options.twistRate = 10
options.useAerodynamicJump = true
options.useSpinDrift = true
options.v0 = 3100
options.wind = 10
options.windDir = 90
//console.log(trajectory(options))
const start_t = process.hrtime()
const start = start_t[0] * 1000000 + start_t[1] / 1000
const base_t = trajectory(options)[options.range]
const end_t = process.hrtime()
const end = end_t[0] * 1000000 + end_t[1] / 1000
console.log('time', end - start, 'us')
for (const name in gs) {
  GRAVITY = gs[name]
  const t = trajectory(options)[options.range]
  console.log(name, '\t', t.dropMOA, t.dropMOA - base_t.dropMOA)
}
*/

/*
const start_t = process.hrtime()
const start = start_t[0] * 1000000 + start_t[1] / 1000
console.log(trajectory({ range: 3000, zeroRange: 3000 })[3000])
const end_t = process.hrtime()
const end = end_t[0] * 1000000 + end_t[1] / 1000
console.log('time', end - start, 'us')
console.log('time', (end - start) / 1000, 'ms')
*/
//console.log(trajectory({ range: 1000, useSpinDrift: false })[1000]);
//console.log(trajectory({ range: 1000, wind: 10, cant: 1, useSpinDrift: false })[1000]);
//console.log(trajectory({ range: 1000, cant: 2, useSpinDrift: false })[1000])
//console.log(trajectory({ range: 1000, cant: 5, useSpinDrift: false })[1000])
